import moment from 'moment';
import Api from '../Api';
import coins from '../common';
import ApexCharts from 'apexcharts';
import { parseUnits, getExplorerLink, submitsToHashrate } from '../utils';

export function createApp(store) {
    // eslint-disable-next-line no-undef
    let app = PetiteVue.createApp({
        store,
        parseUnits,
        getExplorerLink,
        moment,
        partyId: null,
        party: null,
        isLoadingParty: false,
        tillLaunch: "00:00:00",
        hashrate5M: 0,
        hashrate60M: 0,
        isMining: false,
        isStarting: false,
        blocks: [],

        mounted(partyId) {
            this.partyId = partyId;
            this.loadParty();
        },

        async loadParty() {
            this.isLoadingParty = true;
            try {
                this.party = await Api.party.get(this.partyId);
                this.coin = coins.find(coin => coin.code === this.party.coin);
                this.blocks = await Api.blocks.get({ party: this.partyId });
                this.updateTillLaunch();
                this.fetchHashrate();
                this.fetchChartData();

                if (!this.updateInterval) {
                    this.updateInterval = setInterval(() => {
                        this.updateTillLaunch();
                    }, 1000);
                }

                if (!this.hashrateUpdateInterval) {
                    this.hashrateUpdateInterval = setInterval(() => {
                        this.fetchHashrate();
                        this.fetchChartData();
                    }, 10000);
                }

            } catch (error) {
                console.error('Failed to load party', error);
            }
            this.isLoadingParty = false;

        },

        get miners() {
            if (!this.party) return [];
            return this.party.miners.sort((a, b) => b.shares - a.shares);
        },

        get totalShares() {
            if (!this.party) return 0;
            return this.party.miners.reduce((total, miner) => total + miner.shares, 0);
        },

        get totalWork() {
            if (!this.party || !this.party.chainStats) return 0;
            let workPerUse = this.party.oneTicketTH / 0.004294967296;
            let totalWork = workPerUse * this.totalShares;
            return totalWork;
        },

        get chanceToFindBlock() {
            if (!this.totalWork) return 0;
            let chance = this.party.chainStats.difficulty / this.totalWork;
            return "1 : " + Math.round(chance);
        },

        updateTillLaunch() {
            if (!this.party) return;

            let launch = moment(this.party.launchDate);
            let now = moment();
            let diff = launch.diff(now);
            if (diff < 0) {
                this.tillLaunch = 'Starting...';

                if (!this.isStarting && !this.party.startedAt) {
                    this.isStarting = true;
                    setTimeout(() => {
                        // reload page
                        window.location.reload();
                    }, 50000);
                }
                return;
            }
            let duration = moment.duration(diff);
            let hours = Math.floor(duration.asHours());
            let minutes = duration.minutes();
            let seconds = duration.seconds();
            this.tillLaunch = `${hours}:${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
        },

        async fetchHashrate() {
            try {
                const res5M = await Api.party.generatedWork(this.partyId, 5);
                let shares = 0;
                for (const rig in res5M) {
                    shares += res5M[rig].work;
                }

                this.isMining = shares > 0;

                let tmpHashrate = submitsToHashrate(shares, 5*60);
                this.hashrate5M = parseUnits(tmpHashrate);

                const res60M = await Api.party.generatedWork(this.partyId, 60);
                shares = 0;
                for (const rig in res60M) {
                    shares += res60M[rig].work;
                }

                tmpHashrate = submitsToHashrate(shares, 60 * 60);
                this.hashrate60M = parseUnits(tmpHashrate);
            } catch (error) {
                console.warn("Cant load stats", error);
            }
        },

        async fetchChartData() {
            try {
                const chartData = await Api.party.submitsChart(this.partyId);

                if (!this.chart) {
                    this.chart = new ApexCharts(document.querySelector('#bestshare-chart'), chartOptions);
                    this.chart.render();
                }

                if (!this.hashrateChart) {
                    this.hashrateChart = new ApexCharts(document.querySelector('#hashrate-chart'), hashrateChartOptions);
                    this.hashrateChart.render();
                }

                const startDate = new Date();
                startDate.setHours(startDate.getHours() - 12, 0, 0, 0);
                const endDate = new Date();
                const intervalMillis = 1000 * 60 * 15; // 15 minut w milisekundach

                const fullRange = [];
                for (let date = +startDate; date <= +endDate; date += intervalMillis) {
                    fullRange.push(new Date(date));
                }

                const result = fullRange.map(interval => {
                    const found = chartData.find(item => (new Date(item[0])).getTime() === interval.getTime());
                    return [
                        interval,
                        found ? found[1] : 0,
                        found ? found[2] : 0,
                        found ? found[3] : 0
                    ];
                });

                this.chart.updateOptions({
                    annotations: {
                        yaxis: [{
                            y: this.party.chainStats.difficulty,
                            borderColor: '#00E396',
                            label: {
                                borderColor: '#00E396',
                                style: {
                                    color: '#fff',
                                    background: '#00E396'
                                },
                                text: 'Network Difficulty'
                            }
                        }]
                    }
                });

                this.chart.updateSeries([{
                    name: 'Best share',
                    // data: result.map(point => [point[0], point[3]])
                    data: result.map(point => { return {
                        x: point[0],
                        y: point[3],
                        goals: [
                            {
                                name: 'Needed for block',
                                value: this.party.chainStats.difficulty,
                                strokeHeight: 5,
                                strokeColor: '#775DD0'
                            }
                        ]
                    }})
                }]);

                const hashrateData = [];
                const generatedWorkData = [];
                for (let i = 0; i < result.length; i++) {
                    let date = new Date(result[i][0]);

                    if (i == result.length - 1) date = new Date();
                    const work = result[i][2];

                    let timeSec = 15 * 60;
                    let prevWork = 0;
                    if (i > 0) {
                        timeSec = (date - new Date(result[i - 1][0])) / 1000;
                        prevWork = generatedWorkData[i - 1];
                    }

                    const r = Math.round(work * 4294967296 / timeSec);
                    hashrateData.push(r);
                    generatedWorkData.push(prevWork + Math.round(work/1000));
                }

                this.hashrateChart.updateOptions({
                    labels: result.map(d => d[0].toISOString()),
                    series: [{
                        name: 'Hashrate',
                        data: hashrateData
                    }, {
                        name: 'Generated work',
                        data: generatedWorkData
                    }]
                });

            } catch (error) {
                console.warn("Cant load chart data", error);
            }
        }
    });
    return app;
}


const chartOptions = {
    chart: {
        zoom: { enabled: false },
        height: 240,
        type: 'bar',
        parentHeightOffset: 0,
        toolbar: {
            show: false
        },
        animations: {
            enabled: false
        }
    },
    dataLabels: {
        enabled: false
    },
    fill: {
        // opacity: 0.16,
        type: 'solid'
    },
    xaxis: {
        labels: {
            datetimeUTC: false
        },
        tooltip: {
            enabled: false
        },
        axisBorder: {
            show: false
        },
        type: 'datetime'
    },
    yaxis: {
        forceNiceScale: true,
        logarithmic: true,
        logBase: 10,
        labels: {
            formatter: function(val, index) {
                if (val > 1000 * 1000 * 1000) return Math.round(val / 1000000000 * 100) / 100 + 'G';
                else if (val > 1000 * 1000) return Math.round(val / 1000000 * 100) / 100 + 'M';
                else if (val > 1000) return Math.round(val / 1000 * 100) / 100 + 'K';
                else return Math.round(val * 100) / 100;
            }
        }
    },
    tooltip: {
        theme: 'dark',
        x: {
            format: "HH:mm"
        }
    },
    grid: {
        padding: {
            top: -20,
            right: 0,
            left: -4,
            bottom: -4
        },
        strokeDashArray: 4
    },
    legend: { show: false },
    // labels: [],
    series: []
};


const hashrateChartOptions = {
    chart: {
        zoom: { enabled: false },
        type: 'area',
        height: 240,
        parentHeightOffset: 0,
        toolbar: {
            show: false
        },
        animations: {
            enabled: false
        }
    },
    dataLabels: {
        enabled: false
    },
    fill: {
        opacity: 0.16,
        type: 'solid'
    },
    stroke: {
        width: 2,
        lineCap: "round",
        curve: "smooth"
    },
    xaxis: {
        labels: {
            datetimeUTC: false
        },
        tooltip: {
            enabled: false
        },
        axisBorder: {
            show: false
        },
        type: 'datetime'
    },
    yaxis: [{
        seriesName: 'Hashrate',
        title: { text: 'Party hashrate' },
        labels: {
            formatter: function(val, index) {
                if      (val > 1000000000000000) return Math.round(val / 1000000000000000 * 100) / 100 + 'PH/s';
                else if (val > 1000000000000) return Math.round(val / 1000000000000 * 100) / 100 + 'TH/s';
                else if (val > 1000000000) return Math.round(val / 1000000000 * 100) / 100 + 'GH/s';
                else if (val > 1000000) return Math.round(val / 1000000 * 100) / 100 + 'MH/s';
                else if (val > 1000) return Math.round(val / 1000 * 100) / 100 + 'KH/s';
                else return val + 'H/s';
            }
        }
    }, {
        seriesName: 'Generated work',
        opposite: true,
        axisTicks: { show: true },
        title: { text: 'Generated work' },
        labels: {
            formatter: function(val, index) {
                if      (val > 1000000000000) return Math.round(val / 1000000000000 * 100) / 100 + 'P';
                else if (val > 1000000000) return Math.round(val / 1000000000 * 100) / 100 + 'T';
                else if (val > 1000000) return Math.round(val / 1000000 * 100) / 100 + 'G';
                else if (val > 1000) return Math.round(val / 1000 * 100) / 100 + 'M';
                else return val + 'K';
            }
        }
    }],
    tooltip: {
        theme: 'dark',
        x: {
            format: "HH:mm"
        }
    },
    grid: {
        padding: {
            top: -20,
            right: 0,
            left: -4,
            bottom: -4
        },
        strokeDashArray: 4
    },
    legend: { show: false },
    // labels: this.rawData.map(d => d[0].toISOString()),
    series: []
}